import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Code,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiEye } from 'react-icons/fi'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import {
  useQueueQuarantineMessages,
  useQueueQuarantineStatistics,
} from '@/hooks/useQueueQuarantineData'
import type {
  QueueQuarantineCriticality,
  QueueQuarantineMessage,
  QueueQuarantineStatus,
} from '@/types'
import { formatDateTime } from '@/utils/formatUtils'
import { getQueueQuarantineRelatedIds } from './queueQuarantineMonitorUtils'

const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [20, 50, 100] as const

function statusColor(status: QueueQuarantineStatus): string {
  if (status === 'open') return 'red'
  if (status === 'reprocessing') return 'orange'
  return 'green'
}

function criticalityColor(criticality: QueueQuarantineCriticality): string {
  if (criticality === 'financial') return 'red'
  if (criticality === 'business_mutation') return 'orange'
  return 'gray'
}

function hasCapturedDetails(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false
  }
  if (typeof value === 'string') {
    return value.trim().length > 0
  }
  if (Array.isArray(value)) {
    return value.length > 0
  }
  if (typeof value === 'object') {
    return Object.keys(value).length > 0
  }
  return true
}

function formatCapturedDetails(value: unknown): string {
  if (!hasCapturedDetails(value)) {
    return ''
  }
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2)
}

type QueueQuarantineMonitorProps = {
  isActive: boolean
}

export function QueueQuarantineMonitor({ isActive }: QueueQuarantineMonitorProps) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [status, setStatus] = useState('')
  const [queue, setQueue] = useState('')
  const [selectedMessage, setSelectedMessage] =
    useState<QueueQuarantineMessage | null>(null)
  const details = useDisclosure()
  const offset = (page - 1) * pageSize
  const statisticsQuery = useQueueQuarantineStatistics({ enabled: isActive })
  const messagesQuery = useQueueQuarantineMessages(
    {
      limit: pageSize,
      offset,
      status: status || undefined,
      queue: queue.trim() || undefined,
    },
    { enabled: isActive }
  )
  const statusOptions = [
    { value: '', label: t('common.all') },
    ...(['open', 'reprocessing', 'resolved'] as QueueQuarantineStatus[]).map(
      value => ({
        value,
        label: t(`monitoring.queueQuarantine.statuses.${value}`),
      })
    ),
  ]
  const pageSizeOptions = PAGE_SIZE_OPTIONS.map(size => ({
    value: String(size),
    label: String(size),
  }))
  const statistics = statisticsQuery.data ?? []
  const metrics = useMemo(() => {
    const total = statistics.reduce((sum, item) => sum + item.count, 0)
    const countStatus = (value: QueueQuarantineStatus) =>
      statistics
        .filter(item => item.status === value)
        .reduce((sum, item) => sum + item.count, 0)
    const financial = statistics
      .filter(item => item.criticality === 'financial')
      .reduce((sum, item) => sum + item.count, 0)

    return [
      { key: 'total', value: total },
      { key: 'open', value: countStatus('open') },
      { key: 'reprocessing', value: countStatus('reprocessing') },
      { key: 'resolved', value: countStatus('resolved') },
      { key: 'financial', value: financial },
    ]
  }, [statistics])
  const pageData = messagesQuery.data
  const totalPages = Math.max(
    1,
    Math.ceil((pageData?.pagination.total ?? 0) / pageSize)
  )

  const handleRefresh = async () => {
    await Promise.all([statisticsQuery.refetch(), messagesQuery.refetch()])
  }

  const handleRefreshDetails = async () => {
    const selectedId = selectedMessage?.id
    const [, messagesResult] = await Promise.all([
      statisticsQuery.refetch(),
      messagesQuery.refetch(),
    ])
    if (!selectedId) {
      return
    }
    const refreshedMessage = messagesResult.data?.messages.find(
      message => message.id === selectedId
    )
    if (refreshedMessage) {
      setSelectedMessage(refreshedMessage)
    }
  }

  const openDetails = (message: QueueQuarantineMessage) => {
    setSelectedMessage(message)
    details.onOpen()
  }

  return (
    <VStack align="stretch" spacing={4}>
      {statisticsQuery.isLoading && statistics.length === 0 && (
        <HStack justify="center" py={6}>
          <Spinner size="sm" />
          <Text fontSize="sm">{t('common.loading')}</Text>
        </HStack>
      )}

      {statisticsQuery.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {statisticsQuery.error instanceof Error
            ? statisticsQuery.error.message
            : t('common.operationFailed')}
        </Alert>
      )}

      {statisticsQuery.isSuccess && (
        <SimpleGrid columns={{ base: 2, md: 3, xl: 5 }} spacing={3}>
          {metrics.map(metric => (
            <Card key={metric.key} borderRadius="md" variant="outline">
              <CardBody py={2} px={3}>
                <HStack justify="space-between" spacing={2}>
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {t(`monitoring.queueQuarantine.metrics.${metric.key}`)}
                  </Text>
                  <Text fontSize="lg" fontWeight="semibold" lineHeight="1">
                    {metric.value}
                  </Text>
                </HStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}

      <HStack align="end" spacing={3} flexWrap="wrap">
        <FormControl maxW="320px">
          <FormLabel fontSize="xs">
            {t('monitoring.queueQuarantine.filters.queue')}
          </FormLabel>
          <Input
            size="sm"
            value={queue}
            onChange={event => {
              setQueue(event.target.value)
              setPage(1)
            }}
            placeholder={t('monitoring.queueQuarantine.filters.queuePlaceholder')}
          />
        </FormControl>
        <FormControl maxW="220px">
          <FormLabel fontSize="xs">
            {t('monitoring.queueQuarantine.filters.status')}
          </FormLabel>
          <DropdownSelect
            size="sm"
            value={status}
            options={statusOptions}
            onChange={value => {
              setStatus(value)
              setPage(1)
            }}
          />
        </FormControl>
        <RefreshIconButton
          label={t('common.refresh')}
          onClick={handleRefresh}
          isLoading={statisticsQuery.isFetching || messagesQuery.isFetching}
        />
      </HStack>

      {messagesQuery.isLoading && !pageData ? (
        <Box py={10} textAlign="center">
          <Spinner size="md" />
        </Box>
      ) : messagesQuery.error ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {messagesQuery.error instanceof Error
            ? messagesQuery.error.message
            : t('common.operationFailed')}
        </Alert>
      ) : (pageData?.messages.length ?? 0) === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          {t('monitoring.queueQuarantine.empty')}
        </Alert>
      ) : (
        <TableContainer borderWidth="1px" borderRadius="md">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{t('monitoring.queueQuarantine.columns.message')}</Th>
                <Th>{t('monitoring.queueQuarantine.columns.queue')}</Th>
                <Th>{t('monitoring.queueQuarantine.columns.state')}</Th>
                <Th>{t('monitoring.queueQuarantine.columns.related')}</Th>
                <Th>{t('monitoring.queueQuarantine.columns.error')}</Th>
                <Th>{t('monitoring.queueQuarantine.columns.updated')}</Th>
                <Th textAlign="right">
                  {t('monitoring.queueQuarantine.columns.actions')}
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {pageData?.messages.map(message => {
                const relatedIds = getQueueQuarantineRelatedIds(message)
                return (
                  <Tr key={message.id}>
                    <Td>
                      <Code fontSize="xs">{message.id}</Code>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {message.message_type}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="xs">{message.source_queue}</Text>
                      <Text fontSize="xs" color="gray.500">
                        {t('monitoring.queueQuarantine.attempts', {
                          count: message.attempts,
                        })}
                      </Text>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Badge colorScheme={statusColor(message.status)}>
                          {t(
                            `monitoring.queueQuarantine.statuses.${message.status}`
                          )}
                        </Badge>
                        <Badge
                          colorScheme={criticalityColor(message.criticality)}
                          variant="subtle"
                        >
                          {t(
                            `monitoring.queueQuarantine.criticalities.${message.criticality}`
                          )}
                        </Badge>
                      </VStack>
                    </Td>
                    <Td>
                      {relatedIds.length > 0 ? relatedIds.map(item => (
                        <Text key={item.key} fontSize="xs">
                          {item.key}: <Code fontSize="xs">{item.value}</Code>
                        </Text>
                      )) : (
                        <Text fontSize="xs" color="gray.500">
                          {t('common.na')}
                        </Text>
                      )}
                    </Td>
                    <Td maxW="300px">
                      <Text fontSize="xs" fontWeight="semibold">
                        {message.error_class}
                      </Text>
                      <Text fontSize="xs" noOfLines={2}>
                        {message.error_message ?? t('common.na')}
                      </Text>
                    </Td>
                    <Td fontSize="xs">{formatDateTime(message.updated_at)}</Td>
                    <Td textAlign="right">
                      <Tooltip
                        label={t('monitoring.queueQuarantine.viewDetails')}
                        hasArrow
                      >
                        <IconButton
                          aria-label={t(
                            'monitoring.queueQuarantine.viewDetails'
                          )}
                          icon={<FiEye />}
                          size="sm"
                          variant="ghost"
                          onClick={() => openDetails(message)}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {pageData && pageData.pagination.total > 0 && (
        <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
          <HStack spacing={4} flexWrap="wrap">
            <Text fontSize="sm" color="gray.500">
              {t('common.pagination.pageOf', {
                current: page,
                total: totalPages,
                defaultValue: `Page ${page} of ${totalPages}`,
              })}
            </Text>
            <HStack spacing={2}>
              <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                {t('common.pagination.pageSize')}
              </Text>
              <Box w="84px">
                <DropdownSelect
                  size="xs"
                  value={String(pageSize)}
                  options={pageSizeOptions}
                  onChange={value => {
                    setPageSize(Number(value))
                    setPage(1)
                  }}
                />
              </Box>
            </HStack>
          </HStack>
          <HStack>
            <Button
              size="sm"
              onClick={() => setPage(current => Math.max(1, current - 1))}
              isDisabled={page <= 1}
            >
              {t('common.previous')}
            </Button>
            <Button
              size="sm"
              onClick={() => setPage(current => current + 1)}
              isDisabled={!pageData.pagination.hasMore}
            >
              {t('common.next')}
            </Button>
          </HStack>
        </HStack>
      )}

      <Modal
        isOpen={details.isOpen}
        onClose={details.onClose}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pr={12}>
            <HStack justify="space-between" spacing={3}>
              <Text>{t('monitoring.queueQuarantine.detailsTitle')}</Text>
              <RefreshIconButton
                label={t('common.refresh')}
                tooltipPlacement="left"
                onClick={() => void handleRefreshDetails()}
                isLoading={statisticsQuery.isFetching || messagesQuery.isFetching}
                isDisabled={statisticsQuery.isFetching || messagesQuery.isFetching}
                flexShrink={0}
              />
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedMessage && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    {t('monitoring.queueQuarantine.columns.message')}
                  </Text>
                  <Code fontSize="sm">{selectedMessage.id}</Code>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    {t('monitoring.queueQuarantine.payload')}
                  </Text>
                  <Code
                    display="block"
                    p={3}
                    whiteSpace="pre-wrap"
                    overflowWrap="anywhere"
                  >
                    {hasCapturedDetails(selectedMessage.redacted_payload)
                      ? formatCapturedDetails(selectedMessage.redacted_payload)
                      : t('monitoring.queueQuarantine.noPayloadCaptured')}
                  </Code>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    {t('monitoring.queueQuarantine.errorContext')}
                  </Text>
                  <Code
                    display="block"
                    p={3}
                    whiteSpace="pre-wrap"
                    overflowWrap="anywhere"
                  >
                    {hasCapturedDetails(selectedMessage.error_context)
                      ? formatCapturedDetails(selectedMessage.error_context)
                      : t('monitoring.queueQuarantine.noErrorContextCaptured')}
                  </Code>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={details.onClose}>{t('common.close')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
}
